/* The account, on this device: one session string and one masked address.
 *
 * **Why there is an account at all.** A parent who changes phone would
 * otherwise lose every page and every credit they paid for, and no amount of
 * care about the child's name makes that acceptable once money is involved. So
 * the account exists to carry pages and credits across a phone change, and it
 * carries nothing else.
 *
 * **What it is not.** It is not an identity for the child. The child's name is
 * written in `learners.ts` on this device and, since V3, stored with the
 * account as ciphertext so a restored phone can show who is who instead of
 * asking the parent to name their children again. It goes to the two account
 * learner routes and nowhere else: not with a photograph, not in a web
 * address, not in a log line.
 *
 * **No password, anywhere.** A six-digit code to an email address, and signing
 * up and signing in are the same act. A password would be a second secret about
 * a children's product, stored by us, forgotten by parents and reused from
 * elsewhere; the email is already the recovery channel a password would fall
 * back on.
 *
 * **The address written here is the masked one.** The service answers with it
 * already masked ("k***g@boonio.com") and that is the only form this store
 * keeps. The address a parent typed is held in memory and in the sign-in flow's
 * own `sessionStorage` for as long as the code is being typed, and it is posted
 * to `/v1/account/code` and `/v1/account/verify`, so "the client never holds the
 * real address" would be false. What is true is that nothing persists it beside
 * the session. Pass the service's masked string through; never reconstruct one
 * from what a parent typed on the sign-in screen.
 */
import { useSyncExternalStore } from "react";
import { dropOwedYears } from "./learners";

const TOKEN = "mh.session";
const EMAIL = "mh.email";

export interface Session {
  /** The bearer token. It goes in `X-Session` on the account routes and on
   *  nothing else, which `api.ts` enforces by shape. */
  token: string | null;
  /** Masked, as the service sends it. For display and for nothing else. */
  email: string | null;
}

const listeners = new Set<() => void>();
let temporary: Session | null = null;

/** The snapshot has to be referentially stable between changes or
 *  `useSyncExternalStore` re-renders forever. Rebuilt only when something is
 *  written. */
let snapshot: Session = read();

function held(key: string): string | null {
  try {
    const value = localStorage.getItem(key);
    return value && value.length > 0 ? value : null;
  } catch {
    // Safari private browsing throws on read. No session, no account controls,
    // and everything else in the app works exactly as it did before accounts.
    return null;
  }
}

function read(): Session {
  return temporary ?? { token: held(TOKEN), email: held(EMAIL) };
}

function changed(): void {
  snapshot = read();
  for (const fn of listeners) fn();
}

export function session(): string | null {
  return snapshot.token;
}

export function email(): string | null {
  return snapshot.email;
}

export function signedIn(): boolean {
  return snapshot.token !== null;
}

/** Remember a verified session. `maskedEmail` is the service's own string. */
export function remember(token: string, maskedEmail?: string | null): void {
  temporary = { token, email: maskedEmail ?? null };
  try {
    localStorage.setItem(TOKEN, token);
    if (maskedEmail) localStorage.setItem(EMAIL, maskedEmail);
    else localStorage.removeItem(EMAIL);
    temporary = null;
  } catch {
    /* The session lasts this tab and no longer. Nothing else is affected. */
  }
  changed();
}

/** Sign out, and a dead session. The same call for both, because they leave
 *  this device in the same state: the learners, their names, their pictures and
 *  their pages all stay exactly where they are. Signing out is not deleting. */
export function forget(): void {
  temporary = { token: null, email: null };
  try { sessionStorage.removeItem("mh.child-draft"); } catch { /* No draft storage. */ }
  try {
    localStorage.removeItem(TOKEN);
    localStorage.removeItem(EMAIL);
    temporary = null;
  } catch {
    /* nothing to do, and nothing worth telling anyone about */
  }
  /* And the years this account was owed, which are now owed to nobody. The
     queue is keyed on the child and not on the account, so leaving it would
     let the next parent to sign in on this phone receive somebody else's
     children on their account. Signing out is still not deleting: the
     learners, their names and their years all stay. */
  dropOwedYears();
  changed();
}

// Sign-in and explicit sign-out in another tab should update this tab too.
window.addEventListener("storage", event => {
  if (event.storageArea === localStorage && (event.key === TOKEN || event.key === EMAIL || event.key === null)) {
    temporary = null;
    changed();
  }
});

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function get(): Session {
  return snapshot;
}

export function useSession(): Session {
  return useSyncExternalStore(subscribe, get, get);
}
